import { useState } from 'react';
import { faGear } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { usePageSectionContext } from '@/components/page-sections/page-section-context';
import {
  type ReviewAlign,
  type ReviewDisplaySettings,
  type ReviewFieldKey,
  resolveReviewFieldOrder,
  reviewFieldEnabled,
} from '@/components/page-sections/review-widget/variants/review-display';
import { Icon } from '@/components/ui/icon';
import type { CollectionCardField } from '@/lib/blocks/collection/blocks';
import { readRecord } from '@/lib/prop-utils';
import { cn } from '@/lib/utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import type { PageBlock } from '@/types/block';
import { BackgroundTab } from './collection/settings/background-tab';
import { ContentTab } from './collection/settings/content-tab';
import { SettingsPopoverShell } from './collection/settings/settings-popover-shell';
import { SettingsTab } from './collection/settings/settings-tab';
import { BlockActionButton } from './toolbar/block-action-button';

interface ReviewIndexSettingsDialogProps {
  sectionId: string;
  block: PageBlock;
  isVisible: boolean;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const REVIEW_FIELD_LABELS: Record<ReviewFieldKey, string> = {
  rating: 'Star Rating',
  review: 'Review',
  reviewerIcon: 'Reviewer Icon',
  reviewerName: 'Reviewer Name',
};

const REVIEW_FIELD_GROUPS = [{ slots: ['reviewerIcon', 'reviewerName'] }];

export function ReviewIndexSettingsDialog({
  sectionId,
  block,
  isVisible,
  disabled,
  onOpenChange,
}: ReviewIndexSettingsDialogProps) {
  const updateBlock = usePageEditorStore((state) => state.updateBlock);
  const sectionVariant = usePageSectionContext()?.variant;
  const [open, setOpen] = useState(false);

  const display = (readRecord(block.props?.display) as ReviewDisplaySettings | null) ?? {};
  const naturalAlign: ReviewAlign = sectionVariant === 'list' ? 'center' : 'start';
  const cardAlign: ReviewAlign = display.align ?? naturalAlign;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const patchDisplay = (patch: Record<string, unknown>) => {
    updateBlock(sectionId, block.id, {
      props: { ...(block.props ?? {}), display: { ...display, ...patch } },
    });
  };

  const contentFields: CollectionCardField[] = resolveReviewFieldOrder(display, sectionVariant).map(
    (key) => ({
      slot: key,
      kind: 'text',
      label: REVIEW_FIELD_LABELS[key],
      enabled: reviewFieldEnabled(display, key),
    }),
  );

  const handleFieldsChange = (nextFields: CollectionCardField[]) => {
    const fields: Partial<Record<ReviewFieldKey, boolean>> = { ...(display.fields ?? {}) };
    const fieldOrder: ReviewFieldKey[] = [];
    for (const field of nextFields) {
      const key = field.slot as ReviewFieldKey;
      fieldOrder.push(key);
      fields[key] = field.enabled;
    }
    patchDisplay({ fields, fieldOrder });
  };

  return (
    <SettingsPopoverShell
      open={open}
      onOpenChange={handleOpenChange}
      title="Collection Settings"
      trigger={
        <BlockActionButton
          icon={<Icon icon={faGear} />}
          label="Collection Settings"
          srLabel="Edit collection settings"
          showLabel
          isVisible={isVisible}
          disabled={disabled}
          className={cn(open && 'bg-primary/7')}
          onPointerDown={(event) => event.stopPropagation()}
        />
      }
      layoutContent={null}
      showLayoutTab={false}
      contentContent={
        <ContentTab
          fields={contentFields}
          onFieldsChange={handleFieldsChange}
          fieldGroups={REVIEW_FIELD_GROUPS}
          description="Choose which elements appear in your reviews:"
        />
      }
      backgroundContent={
        <BackgroundTab
          variantProps={display as Record<string, unknown>}
          onVariantPropsChange={patchDisplay}
        />
      }
      settingsContent={
        <SettingsTab
          variantProps={display as Record<string, unknown>}
          renderer="grid"
          showColumns={false}
          showImagePosition={false}
          cardAlign={cardAlign}
          onVariantPropsChange={patchDisplay}
          onCardAlignChange={(align) => patchDisplay({ align })}
        />
      }
    />
  );
}
